import React from "react";
import "./HTML_MAIN_CSS.css";

const HTML_SSE = () => {
  return (
    <div className="html-container">
      <h1>HTML Server-Sent Events (SSE)</h1>

      {/* 1. Introduction */}
      <section className="html-section">
        <h2>1. Introduction</h2>
        <p>
          <strong>Server-Sent Events (SSE)</strong> allow a web server to push real-time updates to the browser over a single HTTP connection.
          Unlike WebSockets, SSE is <em>one-way communication</em> from the server to the client.
        </p>
      </section>

      {/* 2. Why Use SSE */}
      <section className="html-section">
        <h2>2. Why Use SSE?</h2>
        <ul>
          <li>Real-time notifications (news feeds, stock prices).</li>
          <li>Live updates for dashboards or social feeds.</li>
          <li>Simpler than WebSockets for one-way streaming.</li>
          <li>Automatic reconnection built into the browser.</li>
        </ul>
      </section>

      {/* 3. Basic Syntax */}
      <section className="html-section">
        <h2>3. Basic Syntax</h2>
        <p>
          SSE is implemented in JavaScript using the <code>EventSource</code> interface.
        </p>
        <pre className="code-block">
          <code>{`// Client-side JavaScript
const eventSource = new EventSource("server-sse.php");

eventSource.onmessage = function(event) {
  console.log("Message from server:", event.data);
};`}</code>
        </pre>
      </section>

      {/* 4. Example - HTML + Client */}
      <section className="html-section">
        <h2>4. Example: HTML Client</h2>
        <pre className="code-block">
          <code>{`<!DOCTYPE html>
<html>
  <body>
    <h2>Server-Sent Events Example</h2>
    <div id="messages"></div>

    <script>
      const eventSource = new EventSource("server-sse.php");

      eventSource.onmessage = function(event) {
        const messagesDiv = document.getElementById("messages");
        const p = document.createElement("p");
        p.textContent = event.data;
        messagesDiv.appendChild(p);
      };
    </script>
  </body>
</html>`}</code>
        </pre>
      </section>

      {/* 5. Example - Server Side */}
      <section className="html-section">
        <h2>5. Example: Server Script (PHP)</h2>
        <pre className="code-block">
          <code>{`<?php
// server-sse.php
header('Content-Type: text/event-stream');
header('Cache-Control: no-cache');

$counter = rand(1, 100);

while (true) {
    echo "data: The current count is {$counter}\n\n";
    ob_flush();
    flush();
    $counter++;
    sleep(2); // Wait 2 seconds before sending next event
}
?>`}</code>
        </pre>
        <p>
          The server continuously sends updates, which are received in real-time by the client.
        </p>
      </section>

      {/* 6. Event Types */}
      <section className="html-section">
        <h2>6. Event Types</h2>
        <ul>
          <li><code>onmessage</code>: Receives standard messages from the server.</li>
          <li><code>onopen</code>: Fired when connection is established.</li>
          <li><code>onerror</code>: Fired when an error occurs.</li>
          <li>Custom events: <code>addEventListener("customEvent", callback)</code></li>
        </ul>
      </section>

      {/* 7. Sending Custom Events */}
      <section className="html-section">
        <h2>7. Sending Custom Events</h2>
        <p>Server can send named events:</p>
        <pre className="code-block">
          <code>{`// Server-side
echo "event: update\n";
echo "data: New update received!\n\n";

// Client-side
eventSource.addEventListener("update", function(event) {
  console.log("Custom event:", event.data);
});`}</code>
        </pre>
      </section>

      {/* 8. Advantages */}
      <section className="html-section">
        <h2>8. Advantages</h2>
        <ul>
          <li>Simpler than WebSockets for one-way streaming.</li>
          <li>Automatic reconnection by the browser.</li>
          <li>Lightweight and efficient for server-to-client updates.</li>
          <li>Works over standard HTTP, no special protocol required.</li>
        </ul>
      </section>

      {/* 9. Limitations */}
      <section className="html-section">
        <h2>9. Limitations</h2>
        <ul>
          <li>One-way communication only (server → client).</li>
          <li>Not supported in older browsers like Internet Explorer.</li>
          <li>Requires server configuration to handle long-lived connections.</li>
          <li>Not ideal for high-frequency, bidirectional messaging (WebSockets preferred).</li>
        </ul>
      </section>

      {/* 10. Summary */}
      <section className="html-section">
        <h2>10. Summary</h2>
        <ul>
          <li>SSE provides real-time server-to-client updates.</li>
          <li>Uses <code>EventSource</code> for client-side implementation.</li>
          <li>Supports automatic reconnection and custom events.</li>
          <li>Best for dashboards, notifications, and live feeds.</li>
        </ul>
      </section>
    </div>
  );
};

export default HTML_SSE;
